import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CTASection } from "@/components/CTASection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import heroImage from "@/assets/steps-stoops-masonry-wayne-nj.webp";
// Placeholder imports — replace with actual images when provided
import placeholderImg1 from "@/assets/stoop-steps-masonry-contractor-wood-ridge-nj.webp";
import placeholderImg2 from "@/assets/steps-stoops-masonry-wayne-nj.webp";
import placeholderImg3 from "@/assets/steps-stoop-retaining-wall-nutley-nj.webp";
import beforeAfterImg from "@/assets/before-after-brick-steps-north-caldwell-nj-t.webp";

const BASE_URL = "https://masonry-pro.com";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline:
    "What Is a Stoop? The History Behind North Jersey's Favorite Front Entry",
  image: `${BASE_URL}/assets/steps-stoops-masonry-wayne-nj.webp`,
  datePublished: "2026-03-27",
  dateModified: "2026-03-27",
  author: {
    "@type": "Organization",
    name: "Masonry Pro Construction",
    url: BASE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: "Masonry Pro Construction",
    url: BASE_URL,
  },
  description:
    "The word \"stoop\" comes from the Dutch \"stoep\" and has deep roots in North Jersey architecture. Learn what makes a stoop different from regular steps, why they matter, and when they need repair.",
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is a stoop?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "A stoop is a small raised platform with steps at the front entrance of a house. The term comes from the Dutch word \"stoep\" and is most commonly used in the Northeast United States, particularly in New Jersey and New York. Unlike a porch, a stoop is typically made of masonry (brick, stone, or concrete) and serves as both a structural entry point and a social gathering spot.",
      },
    },
    {
      "@type": "Question",
      name: "Where does the word stoop come from?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "The word stoop comes from the Dutch \"stoep,\" which referred to a small platform with steps at the front of a house. According to the Online Etymology Dictionary, the American use of the word dates back to 1755, brought to the Mid-Atlantic region by Dutch settlers in the 1600s. The term stuck around in New York, New Jersey, and parts of Pennsylvania long after other Dutch influences faded.",
      },
    },
    {
      "@type": "Question",
      name: "How do I know if my stoop needs repair?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Common signs include crumbling mortar between bricks or stones, cracks running across the top landing or side walls, the stoop pulling away from the house (even a quarter inch), and loose or rocking steps. If your stoop is showing any of these issues, it is worth getting a professional assessment before minor damage becomes a full replacement.",
      },
    },
  ],
};

const faqs = [
  {
    q: "What is a stoop?",
    a: "A stoop is a small raised platform with steps at the front entrance of a house. The term comes from the Dutch word \"stoep\" and is most commonly used in the Northeast United States, particularly in New Jersey and New York. Unlike a porch, a stoop is typically made of masonry (brick, stone, or concrete) and serves as both a structural entry point and a social gathering spot.",
  },
  {
    q: "Where does the word stoop come from?",
    a: "The word stoop comes from the Dutch \"stoep,\" which referred to a small platform with steps at the front of a house. According to the Online Etymology Dictionary, the American use of the word dates back to 1755, brought to the Mid-Atlantic region by Dutch settlers in the 1600s. The term stuck around in New York, New Jersey, and parts of Pennsylvania long after other Dutch influences faded.",
  },
  {
    q: "How do I know if my stoop needs repair?",
    a: "Common signs include crumbling mortar between bricks or stones, cracks running across the top landing or side walls, the stoop pulling away from the house (even a quarter inch), and loose or rocking steps. If your stoop is showing any of these issues, it is worth getting a professional assessment before minor damage becomes a full replacement.",
  },
];

const citations = [
  "Online Etymology Dictionary — \"Stoop\" entry. Confirms 1755 American English origin from Dutch \"stoep.\"",
  "Ephemeral New York — \"How New York Became a Metropolis of Stoops.\" Details Dutch colonial origins and the flood protection purpose.",
  "NJ Department of Community Affairs — Construction Code Communicator (Fall 2023). Confirms NJ frost line depth zones and 36-inch requirement for North Jersey.",
  "Van der Sijs, Nicoline — \"Stoop and Stoep: Two Dutch Nouns in American and South African English Respectively.\" Academic paper tracing the full linguistic history.",
];

/* Alternating image + text row */
function ContentImageRow({
  image,
  alt,
  children,
  reverse = false,
}: {
  image: string;
  alt: string;
  children: React.ReactNode;
  reverse?: boolean;
}) {
  return (
    <div
      className={`flex flex-col ${reverse ? "md:flex-row-reverse" : "md:flex-row"} gap-8 md:gap-12 items-center`}
    >
      <div className="w-full md:w-1/2">
        <img
          src={image}
          alt={alt}
          loading="lazy"
          width={800}
          height={600}
          className="rounded-xl w-full h-auto object-cover shadow-lg"
        />
      </div>
      <div className="w-full md:w-1/2 prose prose-lg prose-invert max-w-none">
        {children}
      </div>
    </div>
  );
}

export default function WhatIsAStoop() {
  return (
    <>
      <Helmet>
        <title>What Is a Stoop? Steps and Stoops in NJ | Masonry Pro</title>
        <meta name="description" content="A stoop is the small set of steps and landing at your front door. Here is why North Jersey homes have them, and when they need repair or full replacement." />
        <meta property="og:description" content="A stoop is the small set of steps and landing at your front door. Here is why North Jersey homes have them, and when they need repair or full replacement." />
        <meta property="og:title" content="What Is a Stoop? Steps and Stoops in NJ | Masonry Pro" />
        <link rel="preload" as="image" href={heroImage} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <meta property="og:image" content="https://masonry-pro.com/og/steps-stoops.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>
      <Header />
      <Breadcrumb />

      <main className="bg-background">
        {/* Hero */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <img
            src={heroImage}
            alt="Masonry steps and stoop installation in Wayne, NJ by Masonry Pro Construction"
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1080}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-background/80" />
          <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl">
            <p className="text-secondary font-semibold mb-3 uppercase tracking-wider text-sm">
              Pro Tips
            </p>
            <h1 className="text-3xl md:text-5xl font-bold mb-4">
              What Is a{" "}
              <span className="text-gradient-gold">Stoop</span>? The History
              Behind North Jersey's Favorite Front Entry
            </h1>
            <p className="text-lg text-muted-foreground mt-4 max-w-2xl mx-auto">
              If you grew up in North Jersey, you already know what a stoop is.
              Here's the history behind the name, why they matter, and how to
              know when yours needs work.
            </p>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-16 md:py-24">
          <div className="container mx-auto px-4 max-w-6xl space-y-20">
            {/* Intro text */}
            <div className="max-w-3xl mx-auto prose prose-lg prose-invert">
              <p>
                If you grew up in North Jersey, you already know what a stoop
                is. You may have sat on one as a kid. Your parents may have
                stood on it talking to the neighbors. It's where people leave
                packages and where everyone ends up when they stop by. If you
                try to explain a stoop to someone from the Midwest or the West
                Coast, you'll probably get a confused look. "You mean your front
                porch?" Not exactly.
              </p>
            </div>

            {/* Section 1: Where the Word Comes From */}
            <ContentImageRow
              image={placeholderImg1}
              alt="Historic stoop architecture in North Jersey"
            >
              <h2 className="text-2xl font-bold text-foreground mt-0">
                Where the Word "Stoop" Comes From
              </h2>
              <p>
                The word stoop comes from the Dutch "stoep," which referred to a
                small platform with steps at the front of a house. According to
                the Online Etymology Dictionary, the American use of "stoop"
                dates back to 1755, borrowed from Dutch settlers who brought the
                term (and the architectural feature) to the Mid-Atlantic region
                in the 1600s.
                <sup className="text-secondary">1</sup> It stuck around in New
                York, New Jersey, and parts of Pennsylvania long after other
                Dutch influences faded.
              </p>
              <p>
                There's actually a practical reason behind the original design.
                In Holland, homes were built with a raised front stoep to keep
                parlor floors above flood level.
                <sup className="text-secondary">2</sup> When early Dutch
                settlers built homes in the Mid-Atlantic, they carried that same
                design with them. Even though the flooding risk was different
                here, the elevated entry stuck as an architectural standard.
              </p>
            </ContentImageRow>

            {/* Section 2: Why Stoops Are More Common in the Northeast */}
            <ContentImageRow
              image={placeholderImg2}
              alt="Brick and stone stoops in North Jersey neighborhood"
              reverse
            >
              <h2 className="text-2xl font-bold text-foreground mt-0">
                Why Stoops Are More Common in the Northeast
              </h2>
              <p>
                Homes in the Northeast tend to sit higher off the ground than
                homes in the South or out West. There are a few reasons for
                that. Building codes require foundations to extend below the
                frost line, which in North Jersey is 36 inches deep.
                <sup className="text-secondary">3</sup> That puts the first
                floor well above grade, which means you need{" "}
                <Link
                  to="/masonry-services/steps-stoops"
                  className="text-secondary hover:text-secondary/80 underline"
                >
                  masonry steps
                </Link>{" "}
                to get to the front door.
              </p>
              <p>
                The architectural styles common to the area reinforce this.
                Colonials, Cape Cods, Tudors, and Split-Levels all feature
                raised entries with masonry stoops. Walk through neighborhoods
                in Montclair, Nutley, Glen Ridge, or Clifton, and you'll see
                brick, bluestone, and concrete stoops on nearly every block.
              </p>
              <p>
                In areas with milder climates and shallower foundations, homes
                sit closer to ground level. A single step or a flush entry is
                more common. That's part of why the stoop is a distinctly
                Northeastern feature.
              </p>
            </ContentImageRow>

            {/* Section 3: What Your Stoop Goes Through */}
            <ContentImageRow
              image={placeholderImg3}
              alt="Stoop showing wear from North Jersey weather"
            >
              <h2 className="text-2xl font-bold text-foreground mt-0">
                What Your Stoop Goes Through Every Year
              </h2>
              <p>
                Here's the thing that most homeowners don't think about: your
                stoop takes more punishment than almost any other exterior
                surface on your property. Every person who enters and exits your
                home walks on it multiple times a day. In winter, it gets hit
                with ice, snow, rock salt, and calcium chloride.
              </p>
              <p>
                The freeze-thaw cycle in North Jersey can repeat dozens of times
                between November and March. Water can seep into mortar joints
                and hairline cracks, freeze, expand, and force those cracks
                wider over time. Every winter can make it a little worse.
              </p>
              <p>
                On top of that, stoops are structural. They bear the load. When
                the footings beneath settle or shift (common in clay-heavy soil
                across Passaic and Bergen counties), the entire structure can
                pull away from the house. You'll see a gap open up between the
                stoop and the foundation wall. That gap lets water in, which
                accelerates the problem, the same reason{" "}
                <Link
                  to="/waterproofing-services/basement-waterproofing"
                  className="text-secondary hover:text-secondary/80 underline"
                >
                  basement waterproofing
                </Link>{" "}
                is so critical in this region.
              </p>
            </ContentImageRow>

            {/* Section 4: Signs & Repair — full width text */}
            <ContentImageRow
              image={beforeAfterImg}
              alt="Before and after brick steps repair in North Caldwell, NJ by Masonry Pro Construction"
              reverse
            >
              <h2 className="text-2xl font-bold text-foreground mt-0">
                Signs Your Stoop Needs Attention
              </h2>
              <p>Some of these are obvious, some are easy to miss.</p>
              <p>
                Crumbling mortar between the bricks or stones is usually the
                first sign. It looks minor, but it means water is getting into
                the joints and breaking them down from the inside. Cracks
                running across the top landing or down the side walls are
                another indicator. If the stoop is pulling away from the house
                (even a quarter inch), that's a structural concern.
              </p>
              <p>
                Loose or rocking steps are a safety issue. A visitor, a delivery
                driver, or a family member stepping on an unstable tread in
                winter is an accident waiting to happen. If you're noticing any
                of these, it's worth getting it looked at sooner rather than
                later. A{" "}
                <Link
                  to="/masonry-services/steps-stoops-repair"
                  className="text-secondary hover:text-secondary/80 underline"
                >
                  targeted repair
                </Link>{" "}
                now can save you from a full replacement down the road.
              </p>
            </ContentImageRow>

            <div className="max-w-3xl mx-auto prose prose-lg prose-invert">
              <h2 className="text-2xl font-bold text-foreground">
                Repair vs. Replacement: How to Know
              </h2>
              <p>
                Not every stoop problem means starting over. If the footings are
                solid and the structure isn't shifting, repointing the mortar
                joints, resetting a few stones, or patching surface damage can
                extend your stoop's life by years. But if the footings have
                failed, the structure has separated from the house, or the
                damage is widespread, a rebuild is usually the smarter
                investment. Pouring money into repairs on a failing foundation
                just delays the inevitable.
              </p>
              <p>
                We'll give you an honest answer on which route makes sense.
                We've repaired and rebuilt stoops across Bergen, Morris, Essex,
                and Passaic counties for over 35 years, alongside{" "}
                <Link
                  to="/concrete-services/driveways"
                  className="text-secondary hover:text-secondary/80 underline"
                >
                  concrete driveways
                </Link>
                , walkways, and every other piece of the exterior. We know what's worth
                saving and what needs to start fresh.
              </p>

              <h2 className="text-2xl font-bold text-foreground mt-12">
                The Stoop Isn't Going Anywhere
              </h2>
              <p>
                Trends come and go in home design, but the stoop is a permanent
                fixture in North Jersey. It's part of how homes have been built
                in the area. It's part of the culture. And when it's done right,
                with proper footings, quality materials, and craftsmanship that
                accounts for our climate, it can last as long as the house
                itself.
              </p>
              <p>
                If your stoop is showing its age, or if you're building new and
                want an entry that fits the character of your home, give us a
                call or text at{" "}
                <a
                  href="tel:+19737252395"
                  className="text-secondary hover:text-secondary/80 underline"
                >
                  (973) 725-2395
                </a>
                . Free estimate, no trip fee. We'll come take a look and talk
                through what makes sense for your{" "}
                <Link
                  to="/services"
                  className="text-secondary hover:text-secondary/80 underline"
                >
                  masonry
                </Link>{" "}
                project.
              </p>
            </div>

            {/* FAQ Section */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Frequently Asked{" "}
                <span className="text-gradient-gold">Questions</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-border bg-card p-6 flex flex-col"
                  >
                    <h3 className="text-lg font-bold text-foreground mb-3">
                      {faq.q}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </article>

        {/* Citations Accordion */}
        <section className="py-12">
          <div className="container mx-auto px-4 max-w-3xl">
            <Accordion type="single" collapsible>
              <AccordionItem value="citations">
                <AccordionTrigger className="text-muted-foreground text-sm">
                  Source Citations
                </AccordionTrigger>
                <AccordionContent>
                  <ol className="list-decimal list-inside space-y-2 text-muted-foreground text-xs">
                    {citations.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ol>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>

        {/* Navigation links */}
        <section className="pb-8">
          <div className="container mx-auto px-4 max-w-3xl flex flex-wrap gap-6 justify-between">
            <Link
              to="/pro-tips"
              className="text-secondary hover:text-secondary/80 font-semibold inline-flex items-center gap-2"
            >
              ← Back to Pro Tips
            </Link>
            <Link
              to="/pro-tips/benefits-of-adding-a-paver-patio"
              className="text-secondary hover:text-secondary/80 font-semibold inline-flex items-center gap-2"
            >
              Related: Benefits of Adding a Paver Patio →
            </Link>
          </div>
        </section>

        <CTASection />
      </main>
      <Footer />
    </>
  );
}
